<template>
    <div>
        <button
            :class="[
                `relative focus:outline-none`,
                blockClass,
                textClass,
                sizeClass,
                typeClass,
                backgroundClass,
                disabledClass,
                roundedClass,
                shadowClass,
            ]"
            :disabled="isDisabled"
            type="button"
            @click="$emit('click', $event)"
        >
            <span class="whitespace-nowrap">
                <slot v-if="iconPosition === 'right'" />
                <span v-else />
            </span>
            <v-icon
                v-if="icon && !block"
                :class="[iconPosition === 'left' ? 'mr-2' : 'ml-2']"
                size="5"
                :type="icon"
            />
            <span class="whitespace-nowrap">
                <slot v-if="iconPosition === 'left'" />
                <span v-else />
            </span>
            <v-loading-in-place
                v-if="loading"
                :class="`absolute inline ${loadingPadding} mr-2`"
                :size="loadingSize"
            />
        </button>
    </div>
</template>

<script>
    import VIcon from '@/components/elements/VIcon.vue';
    import VLoadingInPlace from '@/components/elements/VLoadingInPlace.vue';
    import advisory_partner_mixin from '@/mixins/advisory_partner_mixin';

    export default {
        components: {
            VLoadingInPlace,
            VIcon,
        },
        mixins: [
            advisory_partner_mixin,
        ],
        props: {
            block: {
                default: false,
                type: Boolean,
            },
            disabled: {
                default: false,
                type: Boolean,
            },
            gradient: {
                default: false,
                type: Boolean,
            },
            icon: {
                default: null,
                type: [String, null],
            },
            loading: {
                default: false,
                type: Boolean,
            },
            iconPosition: {
                default: 'left',
                type: String,
            },
            narrow: {
                default: false,
                type: Boolean,
            },
            rounded: {
                default: false,
                type: Boolean,
            },
            size: {
                default: 'lg',
                type: String,
            },
            text: {
                default: 'base',
                type: String,
            },
            type: {
                default: 'secondary',
                type: String,
                validate() {
                    return ['primary', 'secondary', 'link', 'shadow'].includes(this.type);
                },
            },
        },
        computed: {
            backgroundClass() {
                if (this.disabled && this.type !== 'link') {
                    return `border-gray-300 ring-1 ring-gray-300 bg-white hover:bg-gray-50 
                        focus:ring-offset-2 focus:ring-${this.colorScheme}-500`;
                }
                const data = {
                    'link': '',
                    'primary': this.gradient
                        ? `bg-gradient-to-r from-${this.colorScheme}-600 to-${this.colorScheme}-400`
                        : `bg-${this.colorScheme}-600`,
                    'secondary': this.gradient
                        ? `bg-gradient-to-r from-${this.colorScheme}-100 to-${this.colorScheme}-300`
                        : `bg-${this.colorScheme}-100`,
                    'shadow': 'border border-gray-600',
                };
                return data[this.type];
            },
            blockClass() {
                return this.block ? 'w-full' : 'inline-flex items-center';
            },
            disabledClass() {
                return this.isDisabled ? 'cursor-not-allowed' : '';
            },
            loadingPadding() {
                const data = {
                    'xs': 'right-0',
                    'sm': 'right-0',
                    'md': 'right-0',
                    'lg': 'right-1',
                    'xl': 'right-1',
                };
                return data[this.size];
            },
            loadingSize() {
                const data = {
                    'xs': '2',
                    'sm': '3',
                    'md': '3',
                    'lg': '4',
                    'xl': '5',
                };
                return data[this.size];
            },
            isDisabled() {
                return this.disabled || this.loading;
            },
            roundedClass() {
                return this.rounded ? 'rounded-full' : '';
            },
            textClass() {
                let result = `text-${this.text}`;
                if (this.type === 'link') {
                    result += ` font-medium`;
                }
                return result;
            },
            shadowClass() {
                return this.type === 'link' ? '' : this.isDisabled ? '' : 'shadow-sm';
            },
            sizeClass() {
                const data = {
                    'xs': this.type === 'link' ? 'px-2 py-0.5 text-xs rounded' : 'px-5 py-0.5 text-xs rounded',
                    'sm': `${this.type === 'link' ? 'px-2' : this.narrow ? 'px-3' : 'px-6'} py-1 text-sm rounded-md`,
                    'md': `${this.type === 'link' ? 'px-2' : this.narrow ? 'px-4' : 'px-9'} py-2 text-sm rounded-md`,
                    'lg': `${this.type === 'link' ? 'px-2' : this.narrow ? 'px-6' : 'px-12'} py-2 text-md rounded-md`,
                    'xl': `${this.type === 'link' ? 'px-2' : this.narrow ? 'px-7' : 'px-15'} py-3 text-lg rounded-md`,
                };
                return data[this.size];
            },
            typeClass() {
                if (this.isDisabled) {
                    return 'text-gray-400';
                }

                const data = {
                    'link': `text-${this.colorScheme}-700 hover:text-${this.colorScheme}-500`,
                    'primary': this.gradient ?
                        `text-white hover:from-${this.colorScheme}-800 hover:to-${this.colorScheme}-800` :
                        `text-white hover:bg-${this.colorScheme}-800`,
                    'secondary': this.gradient ?
                        `text-${this.colorScheme}-700
                        hover:from-${this.colorScheme}-300 hover:to-${this.colorScheme}-300` :
                        `text-${this.colorScheme}-700 hover:bg-${this.colorScheme}-300`,
                    'shadow': 'text-gray-700 hover:bg-gray-200',
                };
                return data[this.type];
            },
        },
    };
</script>
